import { c as _c } from "react/compiler-runtime";
import { IcoChevronLeft16, IcoChevronRight16 } from '@onefootprint/icons';
import type { InvestigationSourceDetail, SourceCitationEvidence } from '@onefootprint/request-types/dashboard';
import { IconButton } from '@onefootprint/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HighlightedText from '../highlighted-text/highlighted-text';
const CitedPassages = t0 => {
  const $ = _c(21);
  const {
    evidences,
    paragraphs
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const [index, setIndex] = usePagedIndex(evidences);
  let t2;
  if ($[1] !== evidences) {
    t2 = evidences.filter(isTextEvidence);
    $[1] = evidences;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const passages = t2;
  if (passages.length === 0) {
    return null;
  }
  const safeIndex = Math.min(index, passages.length - 1);
  const current = passages[safeIndex];
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="text-body-3 text-tertiary">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== passages.length || $[8] !== safeIndex || $[9] !== setIndex) {
    t5 = passages.length > 1 && <PassagePager index={safeIndex} total={passages.length} onIndex={setIndex} />;
    $[7] = passages.length;
    $[8] = safeIndex;
    $[9] = setIndex;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="flex items-center justify-between gap-2">{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = current.data.value as string;
  let t8;
  if ($[14] !== current.data.pointer || $[15] !== paragraphs || $[16] !== t7) {
    t8 = <HighlightedText paragraphs={paragraphs} pointer={current.data.pointer} cited={t7} />;
    $[14] = current.data.pointer;
    $[15] = paragraphs;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t8) {
    t9 = <div className="flex flex-col gap-1 px-3 py-3">{t6}{t8}</div>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
type TextEvidence = Extract<SourceCitationEvidence, {
  kind: 'json';
}>;
const isTextEvidence = (evidence: SourceCitationEvidence): evidence is TextEvidence => {
  return evidence.kind === 'json' && typeof evidence.data.value === 'string';
};
export const hasTextPassages = (evidences: SourceCitationEvidence[]) => {
  return evidences.some(isTextEvidence);
};
export const resolvedEvidence = (source: InvestigationSourceDetail) => {
  return source.citations.map(citation => citation.evidence).filter((evidence): evidence is SourceCitationEvidence => !!evidence);
};
export const usePagedIndex = evidences => {
  const $ = _c(2);
  const [index, setIndex] = useState(0);
  const [previousEvidences, setPreviousEvidences] = useState(evidences);
  if (previousEvidences !== evidences) {
    setPreviousEvidences(evidences);
    setIndex(0);
  }
  let t0;
  if ($[0] !== index) {
    t0 = [index, setIndex];
    $[0] = index;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0 as const;
};
export const PassagePager = t0 => {
  const $ = _c(32);
  const {
    index,
    total,
    onIndex
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp2);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = index === 0;
  const t4 = index === 0;
  let t5;
  if ($[3] !== index || $[4] !== onIndex) {
    t5 = () => index > 0 && onIndex(index - 1);
    $[3] = index;
    $[4] = onIndex;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = <IconButton aria-label={t2} aria-disabled={t3} data-disabled={t4} icon={IcoChevronLeft16} className="max-md:size-9" size="tiny" variant="ghost" onClick={t5} />;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = index + 1;
  let t8;
  if ($[11] !== t || $[12] !== t7 || $[13] !== total) {
    t8 = t(_temp3, {
      current: t7,
      total
    });
    $[11] = t;
    $[12] = t7;
    $[13] = total;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <span aria-live="polite" className="tabular-nums text-body-3 text-tertiary">{t8}</span>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t(_temp4);
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = index >= total - 1;
  const t12 = index >= total - 1;
  let t13;
  if ($[19] !== index || $[20] !== onIndex || $[21] !== total) {
    t13 = () => index < total - 1 && onIndex(index + 1);
    $[19] = index;
    $[20] = onIndex;
    $[21] = total;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t13) {
    t14 = <IconButton aria-label={t10} aria-disabled={t11} data-disabled={t12} icon={IcoChevronRight16} className="max-md:size-9" size="tiny" variant="ghost" onClick={t13} />;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t6 || $[30] !== t9) {
    t15 = <span className="flex items-center gap-0.5">{t6}{t9}{t14}</span>;
    $[28] = t14;
    $[29] = t6;
    $[30] = t9;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
};
export default CitedPassages;
function _temp($) {
  return $.citedPassage;
}
function _temp2($) {
  return $.previousPassage;
}
function _temp3($_0) {
  return $_0.passageCount;
}
function _temp4($_1) {
  return $_1.nextPassage;
}